import React from 'react'
import {
  BarChart3,
  Star,
  TrendingUp,
  Users,
  Award,
  Target,
  Clock,
  Zap,
  CheckCircle,
  Sparkles,
  ArrowUpRight,
  BookOpen,
  MessageSquare,
  ThumbsUp,
  Calendar
} from 'lucide-react'

// 统计卡片组件
const StatCard: React.FC<{
  title: string
  value: string
  subtitle?: string
  icon: React.ReactNode
  color: string
  trend?: string
}> = ({ title, value, subtitle, icon, color, trend }) => (
  <div className="group bg-white rounded-2xl border border-slate-100 p-6 hover:shadow-xl hover:shadow-slate-100/50 hover:border-slate-200 transition-all">
    <div className="flex flex-col items-center text-center">
      <div
        className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${color} flex items-center justify-center text-white shadow-lg mb-4 group-hover:scale-110 transition-transform`}
      >
        {icon}
      </div>
      <div className="text-3xl font-bold text-slate-900 mb-1">{value}</div>
      <div className="text-sm text-slate-500 font-medium">{title}</div>
      {(trend || subtitle) && (
        <div className="mt-2 flex items-center gap-1 text-xs font-medium text-emerald-600">
          {trend && <ArrowUpRight className="w-3.5 h-3.5" />}
          {trend || subtitle}
        </div>
      )}
    </div>
  </div>
)

// AI 建议项组件
const SuggestionItem: React.FC<{
  text: string
  type: 'success' | 'info' | 'warning'
}> = ({ text, type }) => {
  const config = {
    success: {
      dot: 'bg-emerald-500',
      bg: 'bg-emerald-50',
      border: 'border-emerald-100',
      text: 'text-emerald-800'
    },
    info: {
      dot: 'bg-blue-500',
      bg: 'bg-blue-50',
      border: 'border-blue-100',
      text: 'text-blue-800'
    },
    warning: {
      dot: 'bg-amber-500',
      bg: 'bg-amber-50',
      border: 'border-amber-100',
      text: 'text-amber-800'
    }
  }

  const c = config[type]

  return (
    <div
      className={`flex items-start gap-3 p-4 ${c.bg} rounded-xl border ${c.border} hover:shadow-md transition-all`}
    >
      <div className={`w-2.5 h-2.5 ${c.dot} rounded-full mt-1.5 flex-shrink-0`} />
      <span className={`text-sm ${c.text}`}>{text}</span>
    </div>
  )
}

export const TeacherPerformanceAnalysis: React.FC = () => {
  return (
    <div className="space-y-8">
      {/* 头部 */}
      <div className="relative rounded-3xl overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500" />
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMtNi42MjcgMC0xMiA1LjM3My0xMiAxMnM1LjM3MyAxMiAxMiAxMiAxMi01LjM3MyAxMi0xMi01LjM3My0xMi0xMi0xMnptMCAxOGMtMy4zMTQgMC02LTIuNjg2LTYtNnMyLjY4Ni02IDYtNiA2IDIuNjg2IDYgNi0yLjY4NiA2LTYgNnoiIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iLjEiLz48L2c+PC9zdmc+')] opacity-20" />
        <div className="relative px-8 py-10 text-center">
          <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mx-auto mb-4">
            <BarChart3 className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">教学表现分析</h2>
          <p className="text-white/80">全面分析您的教学表现，发现提升空间</p>
        </div>
      </div>

      {/* 核心指标 */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="平均评分"
          value="4.8"
          icon={<Star className="w-6 h-6" />}
          color="from-amber-400 to-orange-500"
          trend="超过 90% 教师"
        />
        <StatCard
          title="学生总数"
          value="156"
          icon={<Users className="w-6 h-6" />}
          color="from-blue-400 to-indigo-500"
          trend="+23 本月新增"
        />
        <StatCard
          title="完课率"
          value="89%"
          icon={<BarChart3 className="w-6 h-6" />}
          color="from-emerald-400 to-teal-500"
          trend="优秀表现"
        />
        <StatCard
          title="月增长率"
          value="+15%"
          icon={<TrendingUp className="w-6 h-6" />}
          color="from-purple-400 to-pink-500"
          trend="持续增长"
        />
      </div>

      {/* 详细数据 */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* 教学成就 */}
        <div className="bg-white rounded-3xl border border-slate-100 p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl flex items-center justify-center text-white shadow-lg">
              <Award className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-slate-900">教学成就</h3>
              <p className="text-sm text-slate-500">已获得的荣誉和认证</p>
            </div>
          </div>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-4 bg-amber-50 rounded-xl border border-amber-100">
              <div className="flex items-center gap-3">
                <Star className="w-5 h-5 text-amber-500" />
                <span className="font-medium text-slate-800">优秀教师</span>
              </div>
              <span className="text-xs bg-amber-200 text-amber-800 px-2 py-1 rounded-full font-medium">
                已认证
              </span>
            </div>
            <div className="flex items-center justify-between p-4 bg-blue-50 rounded-xl border border-blue-100">
              <div className="flex items-center gap-3">
                <ThumbsUp className="w-5 h-5 text-blue-500" />
                <span className="font-medium text-slate-800">高评分教师</span>
              </div>
              <span className="text-xs bg-blue-200 text-blue-800 px-2 py-1 rounded-full font-medium">
                连续 3 月
              </span>
            </div>
            <div className="flex items-center justify-between p-4 bg-purple-50 rounded-xl border border-purple-100">
              <div className="flex items-center gap-3">
                <Users className="w-5 h-5 text-purple-500" />
                <span className="font-medium text-slate-800">人气教师</span>
              </div>
              <span className="text-xs bg-purple-200 text-purple-800 px-2 py-1 rounded-full font-medium">
                TOP 10%
              </span>
            </div>
          </div>
        </div>

        {/* 近期表现 */}
        <div className="bg-white rounded-3xl border border-slate-100 p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-xl flex items-center justify-center text-white shadow-lg">
              <Calendar className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-slate-900">近期表现</h3>
              <p className="text-sm text-slate-500">过去 30 天的数据</p>
            </div>
          </div>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-slate-600">
                <BookOpen className="w-4 h-4" />
                <span className="text-sm">完成课程</span>
              </div>
              <span className="font-bold text-slate-900">48 节</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-slate-600">
                <Clock className="w-4 h-4" />
                <span className="text-sm">教学时长</span>
              </div>
              <span className="font-bold text-slate-900">72 小时</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-slate-600">
                <MessageSquare className="w-4 h-4" />
                <span className="text-sm">学生好评</span>
              </div>
              <span className="font-bold text-slate-900">98.5%</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-slate-600">
                <Users className="w-4 h-4" />
                <span className="text-sm">复购学生</span>
              </div>
              <span className="font-bold text-slate-900">78%</span>
            </div>
          </div>
        </div>
      </div>

      {/* AI 分析建议 */}
      <div className="bg-white rounded-3xl border border-slate-100 p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-gradient-to-br from-indigo-400 to-purple-500 rounded-xl flex items-center justify-center text-white shadow-lg">
            <Sparkles className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-slate-900">AI 分析建议</h3>
            <p className="text-sm text-slate-500">基于您的数据生成的个性化建议</p>
          </div>
        </div>
        <div className="space-y-3">
          <SuggestionItem
            text="您的教学评分超过 90% 的同类教师，继续保持优秀的教学质量！"
            type="success"
          />
          <SuggestionItem
            text="学生完课率较高，说明课程内容设计合理，学生参与度高。"
            type="info"
          />
          <SuggestionItem
            text="建议增加互动环节和实战练习，可进一步提升学生参与度和满意度。"
            type="warning"
          />
          <SuggestionItem
            text="您在周末的课程预约率最高，可以考虑增加周末时段的可用性。"
            type="info"
          />
        </div>
      </div>

      {/* 提升建议 */}
      <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-3xl border border-indigo-100 p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center text-white shadow-lg">
            <Target className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-slate-900">下一步目标</h3>
            <p className="text-sm text-slate-500">AI 为您规划的成长路径</p>
          </div>
        </div>
        <div className="grid gap-4 md:grid-cols-3">
          <div className="bg-white rounded-2xl p-5 border border-indigo-100">
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle className="w-5 h-5 text-emerald-500" />
              <span className="font-semibold text-slate-800">短期目标</span>
            </div>
            <p className="text-sm text-slate-600">本周完成 10 节课程，保持评分 4.8+</p>
          </div>
          <div className="bg-white rounded-2xl p-5 border border-indigo-100">
            <div className="flex items-center gap-2 mb-3">
              <Zap className="w-5 h-5 text-amber-500" />
              <span className="font-semibold text-slate-800">中期目标</span>
            </div>
            <p className="text-sm text-slate-600">本月新增 30 名学生，提升复购率至 85%</p>
          </div>
          <div className="bg-white rounded-2xl p-5 border border-indigo-100">
            <div className="flex items-center gap-2 mb-3">
              <Award className="w-5 h-5 text-purple-500" />
              <span className="font-semibold text-slate-800">长期目标</span>
            </div>
            <p className="text-sm text-slate-600">成为平台 TOP 5% 教师，获得金牌认证</p>
          </div>
        </div>
      </div>
    </div>
  )
}
